import {
  IOrganization,
  IOrgUser,
  ICreateOrganizationDTO,
  IUpdateOrganizationDTO,
} from '_/types/organizations/organization.types';
import zeusSdk from '_/utils/SDKs';

export const fetchAllOrganizations = async (): Promise<IOrganization[]> => {
  const options = {
    queryParameters: {
      sort: 'name',
      sortDirection: 'ASC',
    },
  };

  const response = await zeusSdk.orgs.listAll(options);
  return Array.isArray(response) ? response : (response as any)?.items || [];
};

export const fetchMyOrganizations = async (): Promise<IOrganization[]> => {
  const options = {
    queryParameters: {
      sort: 'name',
      sortDirection: 'ASC',
    },
  };

  const response = await zeusSdk.orgs.allMine(options);
  const organizations = Array.isArray(response) ? response : (response as any)?.items || [];

  // Sort by name and mark as mine
  return organizations.map((org) => ({ ...org, mine: true })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

export const assignOrgToMe = async (params: { org: IOrganization; userId: string }): Promise<void> => {
  const { org, userId } = params;
  await zeusSdk.orgUsers.create({ roles: ['ADMIN'], userId: userId }, { orgSlug: org.slug });
};

export const unassignOrgFromMe = async (params: { orgSlug: string; roleId: string }): Promise<void> => {
  const { orgSlug, roleId } = params;
  await zeusSdk.orgUsers.delete(roleId, { orgSlug: orgSlug });
};

export const fetchOrganizationBySlug = async (orgSlug: string): Promise<IOrganization> => {
  try {
    // zeusSdk.update({ orgSlug });
    const organization = await zeusSdk.orgs.get(orgSlug);
    return organization;
  } catch (error) {
    console.error('Error fetching organization by slug', error);
    throw error;
  }
};

export const fetchOrgReadme = async (orgId: string): Promise<string> => {
  try {
    const readme = await zeusSdk.orgs.getChild(orgId, 'readme');
    return readme?.content || '';
  } catch (error) {
    console.error('Error fetching organization readme', error);
    return '';
  }
};

export const updateOrgReadme = async (orgId: string, content: string): Promise<string> => {
  try {
    const result = await zeusSdk.orgs.updateChild(orgId, 'readme', { content: content });
    return result?.content || content;
  } catch (error) {
    console.error('Error updating organization readme', error);
    throw error;
  }
};

export const fetchOrgUsers = async (orgId: string): Promise<IOrgUser[]> => {
  try {
    const options = {
      queryParameters: {
        limit: 1000,
        sort: 'username',
        sortDirection: 'ASC',
      },
    };

    const result = await zeusSdk.orgs.getChildren(orgId, 'users', options);
    return result?.items || [];
  } catch (error) {
    console.error('Error fetching organization users', error);
    return [];
  }
};

export const createOrganization = async (org: ICreateOrganizationDTO): Promise<IOrganization> => {
  const result = await zeusSdk.orgs.create(org);
  return result;
};

export const updateOrganization = async (orgId: string, org: IUpdateOrganizationDTO): Promise<IOrganization> => {
  const result = await zeusSdk.orgs.patch(orgId, org);
  return result;
};

/**
 * Add a user to an organization
 */
export const addUserToOrg = async (params: { orgSlug: string; userId: string; roles: string[] }): Promise<any> => {
  const { orgSlug, userId, roles } = params;
  return zeusSdk.orgUsers.create({ roles: roles, userId: userId }, { orgSlug: orgSlug });
};

/**
 * Remove a user from an organization
 */
export const removeUserFromOrg = async (params: { orgSlug: string; roleId: string }): Promise<void> => {
  const { orgSlug, roleId } = params;
  await zeusSdk.orgUsers.delete(roleId, { orgSlug: orgSlug });
};

/**
 * Update an organization user's roles
 */
export const updateOrgUserRole = async (params: { orgSlug: string; roleId: string; roles: string[] }): Promise<any> => {
  const { orgSlug, roleId, roles } = params;
  return zeusSdk.orgUsers.patch(roleId, { roles: roles }, { orgSlug: orgSlug });
};
